'use client'

import { usePathname } from 'next/navigation'
import { createContext, useContext, useEffect, useState, type ReactNode } from 'react'

const STORAGE_CURRENT = 'currentPath'
const STORAGE_PREV = 'previousPath'

export type RouteHistoryContextValue = {
  currentPath: string
  previousPath: string | null
}

const RouteHistoryContext = createContext<RouteHistoryContextValue | null>(null)

export function RouteHistoryProvider({ children }: { children: ReactNode }) {
  const pathname = usePathname()

  const [currentPath, setCurrentPath] = useState<string>(pathname)
  const [previousPath, setPreviousPath] = useState<string | null>(null)

  useEffect(() => {
    const storedCurrent = sessionStorage.getItem(STORAGE_CURRENT)

    // Only update if route actually changed
    if (storedCurrent !== pathname) {
      const prev = storedCurrent || null

      setPreviousPath(prev)
      setCurrentPath(pathname)

      sessionStorage.setItem(STORAGE_PREV, prev || '')
      sessionStorage.setItem(STORAGE_CURRENT, pathname)
    }
  }, [pathname])

  // hydrate on first load (important for refresh / direct visit)
  useEffect(() => {
    const storedPrev = sessionStorage.getItem(STORAGE_PREV)
    const storedCurrent = sessionStorage.getItem(STORAGE_CURRENT)

    if (storedCurrent) setCurrentPath(storedCurrent)
    if (storedPrev) setPreviousPath(storedPrev)
  }, [])

  return <RouteHistoryContext.Provider value={{ currentPath, previousPath }}>{children}</RouteHistoryContext.Provider>
}

export function useRouteHistory(): RouteHistoryContextValue {
  const ctx = useContext(RouteHistoryContext)
  if (!ctx) {
    throw new Error('useRouteHistory must be used within a RouteHistoryProvider')
  }

  return ctx
}
